// features/reports/components/PresupuestoGastosReporte.tsx
// ============================================================================
// PRESUPUESTO DE GASTOS del mes (bloque B2, 15-oct-2026) — Reportes › Pronóstico y presupuesto › Resumen del mes.
// Sustituye al recuadro «Gastos programados» (salía vacío): Mabel lo señaló el 15-oct 12:04 como el lugar donde
// quiere ver su presupuesto de gastos. Presupuestado (Finanzas › Presupuesto; comisiones calculadas) vs real
// (Gastos con la regla del mes de nómina) vs diferencia, por categoría › concepto, y total. Lo arma el servidor
// (/finances/presupuesto/gastos, lib/letsmovePresupuestoGastos.js); aquí solo se pinta. Una columna, a todo el ancho.
// 16-oct-2026 (#1423): también lo usa Finanzas › Presupuesto («Comparativo del mes», con `titulo` y sin el botón de capturar).
// 23-oct-2026 (Mabel): «Pauta publicitaria» es un GRUPO — su real es la suma de las pautas por clase y estas se pintan
// debajo, sangradas, como desglose (no suman otra vez: el servidor ya las sacó de `conceptos`).
// ============================================================================
import { Fragment, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calculator, ChevronDown, FileSpreadsheet, Info, PencilLine } from 'lucide-react';
import { moneyEntero as money } from '../../finances/api';
import { getPresupuestoGastos, urlExportPresupuestoGastos, PresupuestoGastos, PGLinea } from '../../finances/presupuestoGastosApi';
import { Skeleton } from '../../../components/Skeleton';
import { readCache, writeCache } from '../../../services/localCache';

const card = 'rounded-2xl border border-line bg-surface p-4 shadow-sm';
const navBtn = 'rounded-lg border border-line bg-surface p-2 text-content-2 transition hover:bg-surface-2';
const signo = (n: number) => (n > 0 ? '+' : n < 0 ? '−' : '') + money(Math.abs(n));
const pctTxt = (p: number | null) => (p == null ? '' : `${p > 0 ? '+' : p < 0 ? '−' : ''}${Math.abs(p).toFixed(1)} %`);
// En GASTOS, pasarse del presupuesto es lo malo (rojo); gastar menos se deja neutro (puede ser que aún no se paga).
const colorDif = (v: number) => (v > 0.005 ? 'text-red-600 dark:text-red-400' : 'text-content-3');

export default function PresupuestoGastosReporte({ mes, titulo = 'Presupuesto de gastos', ocultarCapturar = false, className = 'mt-4' }:
  { mes: string; titulo?: string; ocultarCapturar?: boolean; className?: string }) {
  const navigate = useNavigate();
  const [d, setD] = useState<PresupuestoGastos | null>(() => readCache<PresupuestoGastos>(`rep-pres-gastos-${mes}`) ?? null);
  const [error, setError] = useState<{ status: number; msg: string } | null>(null);
  const [cargando, setCargando] = useState(false);
  const [abierto, setAbierto] = useState(true);
  const [verConceptos, setVerConceptos] = useState(true);

  useEffect(() => {
    let vivo = true;
    const cached = readCache<PresupuestoGastos>(`rep-pres-gastos-${mes}`);
    setD(cached ?? null); setError(null); setCargando(!cached);
    getPresupuestoGastos(mes)
      .then((r) => { if (!vivo) return; setD(r); writeCache(`rep-pres-gastos-${mes}`, r); })
      .catch((e: any) => { if (vivo) setError({ status: Number(e?.status || 0), msg: e?.message || 'No se pudo cargar el presupuesto de gastos' }); })
      .finally(() => { if (vivo) setCargando(false); });
    return () => { vivo = false; };
  }, [mes]);

  const cabecera = (
    <button onClick={() => setAbierto(!abierto)} className="flex w-full items-center justify-between gap-3 text-left">
      <h3 className="font-display text-base font-bold text-content">{titulo}</h3>
      <span className="flex min-w-0 flex-wrap items-center justify-end gap-x-2 gap-y-0.5 text-right text-xs text-content-3">
        {d && d.disponible && !d.vacio && <>presupuestado <b className="text-content">{money(d.totales.presupuesto)}</b> · real <b className="text-content">{money(d.totales.real)}</b></>}
        <ChevronDown size={16} className={`shrink-0 transition ${abierto ? 'rotate-180' : ''}`} />
      </span>
    </button>
  );

  if (error && !d) {
    return (
      <div className={`${card} ${className}`}>
        {cabecera}
        {abierto && <p className="mt-3 flex items-center gap-2 text-sm text-content-2"><Info size={16} className="text-brand-blue" />
          {error.status === 404 ? 'El presupuesto de gastos se activa cuando el servidor termine de actualizarse. Vuelve a intentar más tarde.' : error.msg}</p>}
      </div>
    );
  }
  if (!d) return <Skeleton className={`${className} h-40 w-full rounded-2xl`} />;

  const visibles = (ls: PGLinea[]) => ls.filter((k) => k.presupuesto || k.real);
  const sinPresupuesto = !d.disponible || d.vacio;

  return (
    <div className={`${card} ${className} ${cargando ? 'opacity-60 transition' : 'transition'}`}>
      {cabecera}
      {abierto && (
        <div className="mt-3">
          {sinPresupuesto && (
            <p className="mb-3 rounded-xl border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300">
              {d.aviso || 'Todavía no hay presupuesto de gastos capturado. Se captura en Finanzas › Presupuesto (concepto × mes) y aparece aquí comparado con lo real.'}
            </p>
          )}
          {!sinPresupuesto && (
            <p className="mb-3 text-sm text-content-2">
              Presupuestado <b className="text-content">{money(d.totales.presupuesto)}</b>
              {d.totales.presupuestoCalculado > 0 && <span className="text-content-3"> (incluye {money(d.totales.presupuestoCalculado)} de comisiones calculadas)</span>}
              {' · '}real <b className="text-content">{money(d.totales.real)}</b>
              {' · '}diferencia <b className={colorDif(d.totales.diferencia)}>{signo(d.totales.diferencia)}</b> <span className="text-xs text-content-3">{pctTxt(d.totales.pct)}</span>
              {d.totales.faltaPorPagar > 0 && <>{' · '}falta por pagar <b className="text-brand-blue">{money(d.totales.faltaPorPagar)}</b></>}
              {d.totales.sobregiro > 0 && <>{' · '}<span className="text-red-600 dark:text-red-400">por encima de lo presupuestado {money(d.totales.sobregiro)}</span></>}
            </p>
          )}
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
            <label className="flex items-center gap-2 text-xs text-content-2">
              <input type="checkbox" checked={verConceptos} onChange={(e) => setVerConceptos(e.target.checked)} /> Ver conceptos
            </label>
            <div className="flex items-center gap-2">
              {!ocultarCapturar && <button onClick={() => navigate('/finanzas?tab=presupuesto')} className={`${navBtn} flex items-center gap-1.5 text-xs`}><PencilLine size={14} /> Capturar presupuesto</button>}
              <a href={urlExportPresupuestoGastos(mes)} download title="Descargar Excel del presupuesto de gastos" className={`${navBtn} flex items-center gap-1.5 text-xs`}><FileSpreadsheet size={14} /> Excel</a>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[720px] text-sm">
              <thead>
                <tr className="border-b border-line text-left text-xs uppercase tracking-wide text-content-3">
                  <th className="py-2 pr-2 font-medium">Categoría › concepto</th>
                  <th className="px-2 py-2 text-right font-medium">Presupuesto</th>
                  <th className="px-2 py-2 text-right font-medium">Real</th>
                  <th className="px-2 py-2 text-right font-medium">Diferencia</th>
                  <th className="py-2 pl-2 text-right font-medium">Falta por pagar</th>
                </tr>
              </thead>
              <tbody>
                {d.secciones.map((s) => s.categorias.filter((c) => c.presupuesto || c.real).map((c) => (
                  <Fragment key={`${s.section}-${c.category_id ?? c.name}`}>
                    <tr className="border-b border-line/60 bg-surface-2/40 font-semibold">
                      <td className="py-1.5 pr-2 text-content">{c.name}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-content-2">{money(c.presupuesto)}</td>
                      <td className="px-2 py-1.5 text-right tabular-nums text-content">{money(c.real)}</td>
                      <td className={`px-2 py-1.5 text-right tabular-nums ${colorDif(c.diferencia)}`}>{signo(c.diferencia)} <span className="text-xs font-normal">{pctTxt(c.pct)}</span></td>
                      <td className="py-1.5 pl-2 text-right tabular-nums text-content-2">{c.faltaPorPagar ? money(c.faltaPorPagar) : '—'}</td>
                    </tr>
                    {verConceptos && visibles(c.conceptos).map((k) => (
                      <Fragment key={k.key}>
                      <tr className="border-b border-line/40">
                        <td className="py-1 pl-5 pr-2 text-content-2" title={k.calculado ? k.detalle : k.note || undefined}>
                          <span className="flex items-center gap-1.5">{k.calculado && <Calculator size={12} className="shrink-0 text-brand-blue" />}{k.name}
                            {k.calculado && <span className="text-xs text-content-3">{k.pctComision} %{k.estimado ? ' · estimado' : ''}</span>}
                            {k.grupo && <span className="text-xs text-content-3">suma de las pautas por clase</span>}
                            {!k.presupuesto && k.real > 0 && <span className="text-xs text-amber-700 dark:text-amber-400">sin presupuesto</span>}</span>
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums text-content-3">{k.presupuesto ? money(k.presupuesto) : '—'}</td>
                        <td className="px-2 py-1 text-right tabular-nums text-content-2">{k.real ? money(k.real) : '—'}</td>
                        <td className={`px-2 py-1 text-right tabular-nums ${colorDif(k.diferencia)}`}>{signo(k.diferencia)} <span className="text-xs">{pctTxt(k.pct)}</span></td>
                        <td className="py-1 pl-2 text-right tabular-nums text-content-3">{k.faltaPorPagar ? money(k.faltaPorPagar) : '—'}</td>
                      </tr>
                      {k.grupo && visibles(k.hijos || []).map((h) => (
                        <tr key={h.key} className="border-b border-line/30 text-xs" data-testid="pauta-hijo">
                          <td className="py-0.5 pl-10 pr-2 text-content-3" title={h.note || undefined}>↳ {h.name}</td>
                          <td className="px-2 py-0.5 text-right tabular-nums text-content-3">{h.presupuesto ? money(h.presupuesto) : ''}</td>
                          <td className="px-2 py-0.5 text-right tabular-nums text-content-2">{h.real ? money(h.real) : '—'}</td>
                          <td className="px-2 py-0.5" />
                          <td className="py-0.5 pl-2" />
                        </tr>
                      ))}
                      </Fragment>
                    ))}
                  </Fragment>
                )))}
                {d.sinConcepto && (
                  <tr className="border-b border-line/60">
                    <td className="py-1.5 pr-2 text-content-2">Gastos sin concepto <span className="text-xs text-amber-700 dark:text-amber-400">({d.sinConcepto.n})</span></td>
                    <td className="px-2 py-1.5 text-right text-content-3">—</td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-content">{money(d.sinConcepto.real)}</td>
                    <td className={`px-2 py-1.5 text-right tabular-nums ${colorDif(d.sinConcepto.real)}`}>{signo(d.sinConcepto.real)}</td>
                    <td className="py-1.5 pl-2 text-right text-content-3">—</td>
                  </tr>
                )}
              </tbody>
              <tfoot>
                <tr className="bg-surface-2/70 font-semibold">
                  <td className="py-2 pr-2 text-content">Total gastos</td>
                  <td className="px-2 py-2 text-right tabular-nums text-content-2">{money(d.totales.presupuesto)}</td>
                  <td className="px-2 py-2 text-right tabular-nums text-content">{money(d.totales.real)}</td>
                  <td className={`px-2 py-2 text-right tabular-nums ${colorDif(d.totales.diferencia)}`}>{signo(d.totales.diferencia)} <span className="text-xs">{pctTxt(d.totales.pct)}</span></td>
                  <td className="py-2 pl-2 text-right tabular-nums text-brand-blue">{money(d.totales.faltaPorPagar)}</td>
                </tr>
              </tfoot>
            </table>
          </div>
          <ul className="mt-2 space-y-0.5 text-xs text-content-3">
            {d.notas.map((n) => <li key={n}>{n}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}
